import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const StudyMaterials: React.FC = () => {
  const [hoveredMaterial, setHoveredMaterial] = useState<number | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<number | null>(null);

  const materials = [
    {
      title: "Textbooks",
      description: "Comprehensive textbooks for all JLPT levels and specialized courses",
      icon: "📚",
      link: "/services/textbooks",
      features: [
        "Digital & physical formats",
        "Practice exercises",
        "Audio materials",
        "Study guides"
      ],
      color: "from-rose-400 to-red-500"
    },
    {
      title: "Smart Class",
      description: "Access to our digital learning platform with interactive exercises",
      icon: "📝",
      link: "/services/online-resources",
      features: [
        "Video lessons",
        "Interactive quizzes",
        "Progress tracking",
        "Mobile support"
      ],
      color: "from-blue-400 to-indigo-500"
    },
    {
      title: "Practice Tests",
      description: "Mock exams and practice materials for JLPT preparation",
      icon: "✍️",
      link: "/services/practice-tests",
      features: [
        "Timed tests",
        "Score analysis",
        "Performance reports",
        "Review materials"
      ],
      color: "from-emerald-400 to-teal-500"
    }
  ];

  return (
    <div className="min-h-screen pt-24 relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-gray-900 to-black">
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0]
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-32"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ 
              rotateX: [0, 10, 0],
              y: [0, -10, 0]
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <h2 className="text-6xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.5)]">
              Study Materials
            </h2>
          </motion.div>
          <motion.div
            className="h-1 w-24 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mx-auto mb-6 rounded-full"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            Access high-quality study materials and resources to support your Japanese language learning journey.
          </motion.p>
        </motion.div>

        {/* Materials Section */}
        <div className="max-w-7xl mx-auto relative mb-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {materials.map((material, index) => (
              <motion.div
                key={material.title}
                className="relative group"
                initial={{ opacity: 0, y: 50, rotateY: -30 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                onHoverStart={() => setHoveredMaterial(index)}
                onHoverEnd={() => setHoveredMaterial(null)}
                onClick={() => setSelectedMaterial(selectedMaterial === index ? null : index)}
              >
                {/* 3D Card */}
                <motion.div
                  className="relative transform-gpu"
                  style={{
                    transformStyle: "preserve-3d",
                    transformOrigin: "center center"
                  }}
                  animate={{
                    rotateX: hoveredMaterial === index ? 10 : 0,
                    rotateY: hoveredMaterial === index ? 10 : 0,
                    z: hoveredMaterial === index ? 50 : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Glowing background */}
                  <motion.div
                    className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${material.color} opacity-20 blur-xl`}
                    animate={{
                      scale: hoveredMaterial === index ? 1.1 : 1,
                      opacity: hoveredMaterial === index ? 0.3 : 0.2
                    }}
                  />

                  {/* Content */}
                  <motion.div
                    className="relative p-8 rounded-3xl bg-gray-900/50 backdrop-blur-lg border border-gray-700 overflow-hidden"
                    animate={{
                      y: hoveredMaterial === index ? -10 : 0,
                      scale: hoveredMaterial === index ? 1.02 : 1
                    }}
                  >
                    {/* Icon */}
                    <motion.div 
                      className="text-6xl mb-8"
                      animate={hoveredMaterial === index ? {
                        scale: [1, 1.2, 1],
                        rotateZ: [0, 5, -5, 0]
                      } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {material.icon}
                    </motion.div>

                    {/* Title & Description */}
                    <h3 className="text-3xl font-bold mb-4 text-white">{material.title}</h3>
                    <p className="text-lg text-gray-300 mb-8">{material.description}</p>

                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      {material.features.map((feature, i) => (
                        <motion.div
                          key={i}
                          className="flex items-center text-gray-300"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                        >
                          <motion.div
                            className={`w-2 h-2 rounded-full bg-gradient-to-r ${material.color} mr-4`}
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.5, 1, 0.5]
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              delay: i * 0.2
                            }}
                          />
                          <span>{feature}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Learn More Link */}
                    {/* <Link to={material.link}>
                      <motion.div
                        className="inline-flex items-center text-white font-semibold group"
                        whileHover={{ x: 5 }}
                      >
                        Explore Materials
                        <motion.svg 
                          className={`w-6 h-6 ml-2 group-hover:bg-gradient-to-r ${material.color} bg-clip-text`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                          animate={{ 
                            x: [0, 5, 0]
                          }}
                          transition={{ duration: 1, repeat: Infinity }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </motion.svg>
                      </motion.div>
                    </Link> */}
                  </motion.div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-32"
        >
          {/* <Link to="/contact">
            <motion.button
              className="px-12 py-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-white text-xl font-bold rounded-full relative overflow-hidden group mb-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              Access Materials Now
            </motion.button>
          </Link> */}
        </motion.div>
      </div>
    </div>
  );
};

export default StudyMaterials; 